import { libelleTaux } from '../../lib/caisse';
import { montantLigne } from '../../lib/soumission';

const fr = (n) =>
  (+n || 0).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const cellule = { padding: '5px 6px', borderBottom: '1px solid #E2E8F0', verticalAlign: 'top' };

/**
 * L'offre sur soumission, en A4, telle qu'elle s'imprime ou s'enregistre en PDF.
 *
 * Les variantes et options sont listées à part, sans entrer dans le total :
 * c'est ainsi que le maître d'ouvrage les compare.
 *
 * @param {object} props
 * @param {object} props.brouillon brouillon de l'offre
 * @param {object} props.totaux totaux calculés
 * @param {object} [props.soc] société
 * @param {object} [props.client] destinataire choisi
 * @returns {import('react').ReactElement} la feuille
 */
export function ApercuSoumission({ brouillon, totaux, soc, client }) {
  const comptees = brouillon.lignes.filter((l) => !l.horsTotal);
  const variantes = brouillon.lignes.filter((l) => l.horsTotal);
  const tableau = (lignes, avecMontant) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
      <thead>
        <tr style={{ background: '#F2F5FA', textAlign: 'left' }}>
          <th style={cellule}>Pos.</th>
          <th style={cellule}>Désignation</th>
          <th style={{ ...cellule, textAlign: 'right' }}>Qté</th>
          <th style={cellule}>Unité</th>
          <th style={{ ...cellule, textAlign: 'right' }}>Prix unit.</th>
          {avecMontant && <th style={{ ...cellule, textAlign: 'right' }}>Montant</th>}
        </tr>
      </thead>
      <tbody>
        {lignes.map((l) => (
          <tr key={l.id}>
            <td style={cellule}>{l.code}</td>
            <td style={cellule}>
              {l.d}
              {+l.rem > 0 ? ` (remise ${l.rem} %)` : ''}
            </td>
            <td style={{ ...cellule, textAlign: 'right' }}>{l.pm && !l.q ? 'pm' : l.q}</td>
            <td style={cellule}>{l.u}</td>
            <td style={{ ...cellule, textAlign: 'right' }}>
              {l.p === '' || l.p == null ? '—' : fr(l.p)}
            </td>
            {avecMontant && (
              <td style={{ ...cellule, textAlign: 'right' }}>{fr(montantLigne(l))}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div
      className="a4-sheet"
      style={{
        background: '#fff',
        maxWidth: '210mm',
        margin: '0 auto',
        padding: '14mm 12mm',
        boxSizing: 'border-box',
        color: '#0F172A',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{soc?.nom || ''}</div>
          <div style={{ fontSize: 10, color: '#475569' }}>
            {[soc?.adresse, soc?.npa, soc?.ville].filter(Boolean).join(' ')}
          </div>
        </div>
        {client && (
          <div style={{ fontSize: 10.5, textAlign: 'right' }}>
            <b>{client.nom}</b>
            <div>{client.adresse || ''}</div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>Offre</div>
      <div style={{ fontSize: 11, marginBottom: 2 }}>{brouillon.titre}</div>
      {brouillon.maitreOuvrage && (
        <div style={{ fontSize: 10, color: '#475569', marginBottom: 12 }}>
          Soumission : {brouillon.maitreOuvrage}
        </div>
      )}
      {tableau(comptees, true)}
      <div style={{ marginLeft: 'auto', width: 260, marginTop: 12, fontSize: 11 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span>Total HT</span>
          <b>CHF {fr(totaux.ht)}</b>
        </div>
        {totaux.remise > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>dont remise {brouillon.remisePct} %</span>
            <span>− {fr(totaux.remise)}</span>
          </div>
        )}
        {totaux.detail.map((d) => (
          <div key={d.taux} style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>
              {libelleTaux(d)} sur {fr(d.ht)}
            </span>
            <span>{fr(d.tva)}</span>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            borderTop: '1px solid #0F172A',
            marginTop: 4,
            paddingTop: 4,
            fontWeight: 800,
          }}
        >
          <span>Total TTC</span>
          <span>CHF {fr(totaux.ttc)}</span>
        </div>
      </div>
      {variantes.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 6 }}>
            Variantes et options — non comprises dans le total
          </div>
          {tableau(variantes, true)}
        </div>
      )}
    </div>
  );
}
